import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Scroll } from "phosphor-react";
import { toast } from "react-toastify";
import {
  StackAnalysisContext,
  StackAnalysisActions,
} from "context/stack_analysis";
import { useFieldsQuery } from "hooks/useFields";
import { getField } from "api/Field";
import { addToCart } from "api/Cart";
import { UserContext } from "context/user";
import Button from "components/Button";
import Divider from "components/elements/Divider";
import CheckBox from "components/CheckBox";
import PanelWrapper from "components/elements/Panel";
import StackAccordion from "components/Accordion/components/StackAccordion";
import "./index.scss";
import CartModal from "components/Modal/CartModal";
import { useStackBasePriceQuery } from "hooks/useStackBasePriceQuery";
import { format } from "date-fns";

const ReviewSummary = () => {
  const { state, dispatch } = useContext(StackAnalysisContext);
  const { token } = useContext(UserContext);
  const { selectedFields, selectedDates } = state;
  const { farmId } = useParams();
  const { data } = useFieldsQuery(farmId);
  const [fieldsSelected, setFieldsSelected] = useState(null);
  const [isTermsAndConditionChecked, setTermsAndConditionChecked] =
    useState(false);
  const [cartIsLoading, setCartIsLoading] = useState(false);
  const [openCart, setOpenCart] = useState(false);
  const { data: stackPricing } = useStackBasePriceQuery(token);
  const costPerField =
    stackPricing?.subscriber_discounted_cost ?? stackPricing?.unit_cost;
  const originalCostPerField = stackPricing?.subscriber_discounted_cost
    ? stackPricing?.unit_cost
    : undefined;

  const totalAreaSelected = () => {
    if (selectedFields.length === 0) return 0;

    const fieldSelected = data.features.filter((field) =>
      selectedFields.includes(field.properties.id)
    );
    const totalArea = fieldSelected.reduce(
      (accumulator, feature) => accumulator + feature.properties.area,
      0
    );

    return totalArea.toFixed(1);
  };

  const fetchFieldDetails = useCallback(() => {
    const fieldInfo = [];
    selectedFields.map(async (id) => {
      const fields = await getField(id, token);
      fieldInfo.push(fields);
      return fieldInfo;
    });
    setFieldsSelected(fieldInfo);
  }, [selectedFields, token]);

  useEffect(() => {
    if (selectedFields.length > 0) {
      fetchFieldDetails();
    }
  }, [fetchFieldDetails, selectedFields]);

  const getTotalStackOrder = () => {
    const totalAmount = selectedFields?.length * (costPerField / 100);
    return totalAmount.toFixed(2);
  };

  const prepareSelectedDates = useCallback(() => {
    const formattedSelectedDates = selectedDates.map((dates) => {
      const dateString = `${dates.date} ${dates.year}`;
      return format(new Date(dateString), "yyyy-MM-dd");
    });
    return formattedSelectedDates;
  }, [selectedDates]);

  const handleStackAddToCart = useCallback(async () => {
    setCartIsLoading(true);
    const query = "item_type=stack analysis order";
    const stackOrder = {
      farm_id: parseInt(farmId, 10),
      dates: prepareSelectedDates(),
      fields: selectedFields,
    };

    try {
      const stackItems = await addToCart(stackOrder, query, token);
      if (stackItems) {
        setOpenCart(true);
        setCartIsLoading(false);
      }
    } catch (e) {
      toast.error(e);
    }
  }, [selectedFields, farmId, token, prepareSelectedDates]);

  return (
    <div className="review-summary">
      <div className="left-wrapper">
        <div className="total-price">
          <p className="label">Total Price</p>
          <p className="price">
            {parseFloat(getTotalStackOrder())?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}{" "}
            excluding GST
          </p>
        </div>
        <div className="selected-fields">
          <PanelWrapper label="Selected fields">
            <StackAccordion
              title={selectedFields?.length}
              totalArea={totalAreaSelected()}
              fields={fieldsSelected}
              cost={costPerField}
              originalCost={originalCostPerField}
            />
          </PanelWrapper>
        </div>
        {/* <div className="capture-previews">
          <PanelWrapper label="Capture Previews">
            <div className="capture-wrapper">
              {selectedDates
                .filter((item) => item.date !== "Skipped")
                .map((selectedDate) => (
                  <div className="capture-items">
                    <p className="label">
                      {selectedDate.date}, {selectedDate.year}
                    </p>
                    <div className="image">
                      <img
                        src={selectedDate.imagery}
                        alt={`${selectedDate.date}, ${selectedDate.year}`}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </PanelWrapper>
        </div> */}
      </div>
      <div className="right-wrapper">
        <Scroll size={32} color="#339DFF" className="icon" />
        <h2 className="title">Review Analysis</h2>
        <div className="description">
          <p className="text">
            Review the paddocks and captures that will be combined to produce
            the Stack. If any fields have no crop, We will remove them for that
            particular year.
          </p>
        </div>
        <Divider />
        <div className="terms-condition">
          <CheckBox
            checked={isTermsAndConditionChecked}
            onChange={() =>
              setTermsAndConditionChecked((prevState) => !prevState)
            }
            label="I have read and agree to terms and conditons."
          />
        </div>
        <div className="actions-button">
          <Button
            type="solid"
            label="Add to Cart"
            color="green"
            rounded
            block
            disabled={!isTermsAndConditionChecked}
            loading={cartIsLoading}
            onClick={() => handleStackAddToCart()} // eslint-disable-line no-console
          />
          <Button
            type="outlined"
            label="Back"
            color="white"
            block
            rounded
            onClick={() =>
              dispatch({
                type: StackAnalysisActions.SET_PAGE,
                value: "captures",
              })
            }
          />
        </div>
      </div>
      {openCart && (
        <CartModal isOpen={openCart} onClose={() => setOpenCart(!openCart)} />
      )}
    </div>
  );
};

export default ReviewSummary;
